import type { AssistantAction } from '@prospero/shared/src/assistant/assistant-contract';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import MainLayout from '../../layouts/MainLayout';
import { AssistantShellFrame, useGuideHitNavigation } from './assistant-shell-frame';
import {
  type ManualDraftOverlayCollector,
  ManualDraftOverlayContext,
} from './manual-draft-overlay';

/**
 * Route shell for the HACCP manual draft page (`/doc-generation/:templateId`):
 * the standard layout plus the same assistant FAB and panel as the self-check
 * shell. The action handler also serves the OPEN_MANUAL_SECTION jump — the
 * draft page is the page the chat is mounted on, so the CTA is a state-only
 * navigation the page consumes to scroll/flash the mentioned section. The
 * overlay context is how the page's UNSAVED section edits reach the outgoing
 * chat messages (the panel collects them at send time).
 */
export function DocGenerationAssistantShell() {
  const navigate = useNavigate();
  const navigateToGuideHit = useGuideHitNavigation();
  const [assistantOpen, setAssistantOpen] = useState(false);
  const overlayBoxRef = useRef<ManualDraftOverlayCollector | null>(null);

  const handleOpenGuideHit = useCallback(
    (action: AssistantAction) => {
      navigateToGuideHit(action);
      if (action.kind === 'OPEN_MANUAL_SECTION') {
        // Same page: no route change, only the highlight payload (the nonce
        // lets the draft page handle repeated jumps to the same section).
        // preventScrollReset keeps root-layout's ScrollRestoration from
        // yanking the page to the top: the ONLY scroll here is the one the
        // SectionEditor performs onto the mentioned section.
        navigate(action.url, {
          state: {
            manualHighlight: {
              sectionKey: action.sectionKey,
              quote: action.quote ?? null,
              nonce: Date.now(),
            },
          },
          preventScrollReset: true,
        });
      }
    },
    [navigate, navigateToGuideHit],
  );

  return (
    <ManualDraftOverlayContext.Provider value={overlayBoxRef}>
      <MainLayout />
      <AssistantShellFrame
        onOpenChange={setAssistantOpen}
        onOpenGuideHit={handleOpenGuideHit}
        open={assistantOpen}
      />
    </ManualDraftOverlayContext.Provider>
  );
}
